import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { IRestaurantDetail } from "../models/IRestaurantDetail";
import { IFood } from "../models/IFood";
import { getRestaurantById } from "../services/restaurants";
import { getMenuByRestaurantId } from "../services/food";
import { checkFavorite, toggleFavorite } from "../services/favorite";
import { getCustomerDetailsByToken } from "../services/auth";
import { getUserToken, getDefaultAddress } from "../utils/tokenUtils";
import { getApiErrorMessage } from "../utils/Util";
import { useActiveOrder } from "../utils/ActiveOrderContext";
import { useBasket } from "../utils/BasketContext";
import { useLanguage } from "../context/LanguageContext";
import RestaurantHeader from "../components/RestaurantHeader";
import MobileRestaurantHeader from "../components/MobileRestaurantHeader";
import FoodList from "../components/FoodList";
import DefaultAdressSelectPopup from "../popupPages/DefaultAdressSelectPopup";
import AdresimPopup from "../popupPages/AdresimPopup";
import "../styles/style.css";

function RestaurantDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState<IRestaurantDetail | null>(null);
  const [foods, setFoods] = useState<IFood[]>([]);
  const [loggedIn, setLoggedIn] = useState(false);
  const [hasDefaultAddr, setHasDefaultAddr] = useState(false);
  const [apiError, setApiError] = useState("");
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const { activeOrders, refreshActiveOrder } = useActiveOrder();
  const { basketCount } = useBasket();
  const { t } = useLanguage();

  const defaultAddrTriggerRef = useRef<HTMLButtonElement>(null);
  const newAddrTriggerRef = useRef<HTMLButtonElement>(null);
  const errorTriggerRef = useRef<HTMLButtonElement>(null);
  const shouldOpenNewAddrRef = useRef(false);

  const recheckDefaultAddr = () => {
    setHasDefaultAddr(loggedIn && !!getDefaultAddress());
  };

  useEffect(() => {
    if (apiError) errorTriggerRef.current?.click();
  }, [apiError]);

  useEffect(() => {
    if (getUserToken()) {
      getCustomerDetailsByToken()
        .then(() => {
          setLoggedIn(true);
          setHasDefaultAddr(!!getDefaultAddress());
        })
        .catch(() => {
          setLoggedIn(false);
          setHasDefaultAddr(false);
        });
    }
  }, []);

  useEffect(() => {
    if (id) {
      refreshActiveOrder();
      getRestaurantById(id)
        .then((res) => setRestaurant(res.data))
        .catch((err) => setApiError(getApiErrorMessage(err, t("common.error"))));

      getMenuByRestaurantId(id)
        .then((res) => setFoods(res.data.filter((f) => f.foodIsEnabled)))
        .catch((err) => setApiError(getApiErrorMessage(err, t("common.error"))));
    }
  }, [id]);

  useEffect(() => {
    if (id && loggedIn) {
      checkFavorite(id)
        .then((res) => setIsFavorite(res.data))
        .catch(() => {});
    }
  }, [id, loggedIn]);

  const handleToggleFavorite = async () => {
    if (!id || favoriteLoading) return;
    setFavoriteLoading(true);
    try {
      await toggleFavorite(id);
      setIsFavorite((prev) => !prev);
    } catch {
      // sessizce geç
    } finally {
      setFavoriteLoading(false);
    }
  };

  useEffect(() => {
    if (restaurant) {
      const deliveryTownIds = (restaurant.restourantDeliveryAddress ?? [])
        .filter((a) => a.restourantDeliveryAddressIsEnabled)
        .map((a) => a.townId);
      localStorage.setItem(
        "restaurantDeliveryTownIds",
        JSON.stringify(deliveryTownIds)
      );

      if (loggedIn && !getDefaultAddress()) {
        defaultAddrTriggerRef.current?.click();
      }
    }
  }, [restaurant]);

  useEffect(() => {
    if (!restaurant) return;
    const modalEl = document.getElementById("defaultAddressSelectModal");
    if (!modalEl) return;
    const handleHide = () => {
      recheckDefaultAddr();
      if (shouldOpenNewAddrRef.current) {
        shouldOpenNewAddrRef.current = false;
        newAddrTriggerRef.current?.click();
      }
    };
    modalEl.addEventListener("hidden.bs.modal", handleHide);
    return () => modalEl.removeEventListener("hidden.bs.modal", handleHide);
  }, [restaurant]);

  useEffect(() => {
    if (!restaurant) return;
    const modalEl = document.getElementById("adresimModal");
    if (!modalEl) return;
    const handleHide = () => recheckDefaultAddr();
    modalEl.addEventListener("hidden.bs.modal", handleHide);
    return () => modalEl.removeEventListener("hidden.bs.modal", handleHide);
  }, [restaurant]);

  if (!restaurant)
    return (
      <div className="container py-4">
        {/* Header skeleton */}
        <div className="skeleton mb-3" style={{ height: "180px", borderRadius: "16px" }} />
        <div className="skeleton mb-2" style={{ height: "22px", width: "50%" }} />
        <div className="d-flex gap-2 mb-4">
          <div className="skeleton" style={{ height: "14px", width: "80px" }} />
          <div className="skeleton" style={{ height: "14px", width: "80px" }} />
        </div>
        {/* Food list skeleton */}
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="d-flex gap-3 align-items-center mb-3 p-3 rounded-4 bg-white shadow-sm">
            <div className="skeleton flex-shrink-0" style={{ width: "80px", height: "80px", borderRadius: "12px" }} />
            <div className="flex-grow-1">
              <div className="skeleton mb-2" style={{ height: "15px", width: "60%" }} />
              <div className="skeleton mb-2" style={{ height: "13px", width: "80%" }} />
              <div className="skeleton" style={{ height: "13px", width: "30%" }} />
            </div>
          </div>
        ))}
      </div>
    );

  const maxOrdersReached = activeOrders.length >= 2;
  const restaurantClosed = !restaurant.restourantIsEnabled;
  const orderDisabled = restaurantClosed || !loggedIn || !hasDefaultAddr || maxOrdersReached;

  return (
    <div className="container py-0 py-sm-4">
      {restaurantClosed ? (
        <div className="alert alert-danger py-2 small mb-3">
          <i className="bi bi-shop me-2"></i>{t("restaurants.restaurantClosed")}
        </div>
      ) : !loggedIn ? (
        <div className="alert alert-warning py-2 small mb-3">
          <i className="bi bi-person-x me-2"></i>{t("restaurants.loginToOrder")}
        </div>
      ) : maxOrdersReached ? (
        <div className="alert alert-warning py-2 small mb-3">
          <i className="bi bi-clock-history me-2"></i>{t("restaurants.maxActiveOrders")}
        </div>
      ) : !hasDefaultAddr ? (
        <div className="alert alert-warning py-2 small mb-3">
          <i className="bi bi-geo-alt me-2"></i>{t("restaurants.selectAddress")}
        </div>
      ) : null}

      <div style={restaurantClosed ? { filter: "grayscale(60%) opacity(0.65)", pointerEvents: "none" } : undefined}>
        {/* Mobil header */}
        <div className="d-block d-sm-none">
          <MobileRestaurantHeader
            restaurant={restaurant}
            foods={foods}
            minAmount={restaurant.restourantMinAmount}
            isFavorite={isFavorite}
            onToggleFavorite={handleToggleFavorite}
            favoriteLoading={favoriteLoading}
            loggedIn={loggedIn}
            orderDisabled={orderDisabled}
          />
        </div>
        {/* Desktop header */}
        <div className="d-none d-sm-block">
          <RestaurantHeader
            restaurant={restaurant}
            isFavorite={isFavorite}
            onToggleFavorite={handleToggleFavorite}
            favoriteLoading={favoriteLoading}
            loggedIn={loggedIn}
          />
        </div>
        <FoodList
          foods={foods}
          minAmount={restaurant.restourantMinAmount}
          disabled={orderDisabled}
        />
      </div>

      {!maxOrdersReached && !restaurantClosed && (
        <button
          className="sepet-float-btn"
          data-bs-toggle={orderDisabled ? undefined : "modal"}
          data-bs-target={orderDisabled ? undefined : "#sepetModal"}
          aria-label={t("cart.title")}
          disabled={orderDisabled}
        >
          <i className="bi bi-cart-fill"></i>
          {basketCount > 0 && (
            <span
              className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-white text-danger"
              style={{ fontSize: "0.7rem", fontWeight: 700 }}
            >
              {basketCount}
            </span>
          )}
        </button>
      )}

      <button
        ref={defaultAddrTriggerRef}
        data-bs-toggle="modal"
        data-bs-target="#defaultAddressSelectModal"
        className="d-none"
      />

      <button
        ref={newAddrTriggerRef}
        data-bs-toggle="modal"
        data-bs-target="#adresimModal"
        className="d-none"
      />

      <DefaultAdressSelectPopup
        onSuccess={recheckDefaultAddr}
        onOpenNewAddress={() => {
          shouldOpenNewAddrRef.current = true;
        }}
      />
      <AdresimPopup address={null} onSuccess={recheckDefaultAddr} />

      {/* Hata modalı tetikleyici */}
      <button ref={errorTriggerRef} className="d-none" data-bs-toggle="modal" data-bs-target="#restaurantErrorModal" />

      {/* Hata modalı */}
      <div className="modal fade" id="restaurantErrorModal" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "340px" }}>
          <div className="modal-content rounded-4 shadow">
            <div className="modal-body px-4 py-4 text-center">
              <div className="mb-3">
                <i className="bi bi-exclamation-circle-fill" style={{ fontSize: "2.8rem", color: "#c20e1a" }}></i>
              </div>
              <p className="fw-semibold mb-4">{apiError}</p>
              <button
                type="button"
                className="btn btn-danger w-100 rounded-pill py-2 fw-semibold"
                data-bs-dismiss="modal"
                onClick={() => navigate(-1)}
              >
                Tamam
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RestaurantDetails;
